import { BaseElement } from '../../base-element';
import { trialsApi } from '../../../api/trials.api';
import { entriesApi } from '../../../api/entries.api';
import { termsApi } from '../../../api/terms.api';
import type { EntryUpdateRequestDto, GridSelectionItem, TrialEventSummaryDto } from '../../../api/types';
import {
  formActions,
  formState,
  type ContactUpdate,
  type ContactFields,
  type DogFields,
  type EmergencyContactFields,
  type FeesFields,
  type ValidationError,
} from '../../../state/form.state';
import { toastActions } from '../../../state/toast.state';
import { navigate } from '../../../router';
import {
  registrationActions,
  registrationComputed,
  registrationState,
} from './registration-state';
import { buildErrorMessage } from './sections/validation-summary';
import { calculateFees, createDefaultFeeSchedule, type FeeBreakdown } from '../../../services/fee-calculation';
import './sections/dog-section';
import './sections/contact-section';
import './sections/emergency-section';
import './sections/fees-section';
import './sections/terms-section';
import './sections/class-selections-grid';
import './sections/validation-summary';
import './sections/premium-section';
import '../../ui/dt-card';
import '../../ui/dt-button';
import '../../ui/dt-spinner';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
const zipRegex = /^\d{5}$/;

export class RegistrationPage extends BaseElement {
  private trialEventId = '';
  private autoSaveTimer: number | null = null;
  private selectionSaveTimer: number | null = null;
  private currentFeeBreakdown: FeeBreakdown | null = null;

  static get observedAttributes(): string[] {
    return ['trial-event-id'];
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    if (name === 'trial-event-id') {
      this.trialEventId = newValue || '';
      if (this.isConnected) {
        void this.loadRegistration();
      }
    }
  }

  connectedCallback(): void {
    this.watch(formState, () => this.updateRender());
    this.watch(registrationState.loading, () => this.updateRender());
    this.watch(registrationState.error, () => this.updateRender());
    this.watch(registrationState.trial, () => this.updateRender());
    this.watch(registrationState.formMetadata, () => this.updateRender());
    this.watch(registrationState.terms, () => this.updateRender());
    this.watch(registrationState.validationSummaryVisible, () => this.updateRender());
    this.watch(registrationState.submitErrorMessage, () => this.updateRender());
    this.watch(registrationState.submitSupportId, () => this.updateRender());
    this.updateRender();

    if (this.trialEventId) {
      void this.loadRegistration();
    }
  }

  override disconnectedCallback(): void {
    if (this.autoSaveTimer !== null) {
      window.clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }

    if (this.selectionSaveTimer !== null) {
      window.clearTimeout(this.selectionSaveTimer);
      this.selectionSaveTimer = null;
    }

    super.disconnectedCallback();
  }

  private async loadRegistration(): Promise<void> {
    if (!this.trialEventId) {
      registrationActions.setError('Missing trial identifier.');
      registrationActions.setLoading(false);
      return;
    }

    registrationActions.reset();
    formActions.initializeForTrial(this.trialEventId);
    registrationActions.setLoading(true);
    registrationActions.setError(null);

    try {
      const [trialResponse, metadataResponse, termsResponse, draftResponse] =
        await Promise.all([
          trialsApi.getById(this.trialEventId),
          trialsApi.getRegistrationMetadata(this.trialEventId),
          termsApi.getCurrent(),
          entriesApi.createDraft(this.trialEventId),
        ]);

      if (trialResponse.error || !trialResponse.data) {
        registrationActions.setError('Unable to load trial details. Please try again.');
        return;
      }

      if (metadataResponse.error || !metadataResponse.data) {
        registrationActions.setError('Unable to load registration metadata. Please try again.');
        return;
      }

      if (termsResponse.error || !termsResponse.data) {
        registrationActions.setError('Unable to load registration metadata. Please try again.');
        return;
      }

      if (draftResponse.error) {
        registrationActions.setError('Unable to create a draft entry. Please try again.');
        return;
      }

      registrationActions.setTrial(trialResponse.data);
      registrationActions.setFormMetadata(metadataResponse.data.formMetadata);
      registrationActions.setTerms(termsResponse.data);

      if (draftResponse.data?.entryId) {
        formActions.loadEntry(draftResponse.data.entryId, {});
      }
    } finally {
      registrationActions.setLoading(false);
    }
  }

  private recalculateFees(): void {
    const state = formState.get();
    const feeSchedule = createDefaultFeeSchedule();
    const isJunior = !!state.contact.junior.dob;
    const pricingTier = 'PreEntry' as const;

    this.currentFeeBreakdown = calculateFees(
      state.selections,
      feeSchedule,
      pricingTier,
      isJunior
    );

    const totalStr = this.currentFeeBreakdown.total.toString();
    if (state.fees.totalEntryFees !== totalStr) {
      formActions.updateFees({ totalEntryFees: totalStr });
    }
  }

  private formatDateRange(trial: TrialEventSummaryDto): string {
    if (!trial.startDate || !trial.endDate) {
      return 'Date TBD';
    }

    const start = new Date(trial.startDate);
    const end = new Date(trial.endDate);
    const sameDay = trial.startDate === trial.endDate;
    const sameMonth = start.getMonth() === end.getMonth();

    const startFormat = start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const endFormat = end.toLocaleDateString('en-US', {
      month: sameMonth ? undefined : 'short',
      day: 'numeric',
      year: 'numeric',
    });

    if (sameDay) {
      return start.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }

    return `${startFormat} - ${endFormat}`;
  }

  private parseNumber(value: string): number | null {
    if (!value) return null;
    const numeric = Number(value);
    return Number.isNaN(numeric) ? null : numeric;
  }

  private isPastDate(value: string): boolean {
    if (!value) return false;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return parsed < today;
  }

  private shouldInclude(path: string, includeAll: boolean, touched: Set<string>): boolean {
    return includeAll || touched.has(path);
  }

  private addError(
    errors: ValidationError[],
    path: string,
    code: string,
    includeAll: boolean,
    touched: Set<string>
  ): void {
    if (!this.shouldInclude(path, includeAll, touched)) return;
    errors.push({
      field: path,
      code,
      message: buildErrorMessage(path, code),
    });
  }

  private validateForm(options: {
    includeAll?: boolean;
    includeTerms?: boolean;
    includeSelections?: boolean;
  } = {}): ValidationError[] {
    const state = formState.get();
    const includeAll = options.includeAll ?? false;
    const touched = registrationState.touchedFields.get();
    const errors: ValidationError[] = [];

    const requiredDog: Array<[string, string]> = [
      ['dog.ascaRegistrationNumber', state.dog.ascaRegistrationNumber],
      ['dog.breed', state.dog.breed],
      ['dog.registeredName', state.dog.registeredName],
      ['dog.callName', state.dog.callName],
      ['dog.dob', state.dog.dob],
      ['dog.sex', state.dog.sex],
    ];

    requiredDog.forEach(([path, value]) => {
      if (!value.trim()) {
        this.addError(errors, path, 'required', includeAll, touched);
      }
    });

    if (state.dog.dob && !this.isPastDate(state.dog.dob)) {
      this.addError(errors, 'dog.dob', 'pastDate', includeAll, touched);
    }

    const requiredContact: Array<[string, string]> = [
      ['contact.owners', state.contact.owners],
      ['contact.ownerAddress.street', state.contact.ownerAddress.street],
      ['contact.ownerAddress.city', state.contact.ownerAddress.city],
      ['contact.ownerAddress.state', state.contact.ownerAddress.state],
      ['contact.ownerAddress.zip', state.contact.ownerAddress.zip],
      ['contact.email', state.contact.email],
      ['contact.phone', state.contact.phone],
    ];

    requiredContact.forEach(([path, value]) => {
      if (!value.trim()) {
        this.addError(errors, path, 'required', includeAll, touched);
      }
    });

    if (state.contact.email && !emailRegex.test(state.contact.email)) {
      this.addError(errors, 'contact.email', 'email', includeAll, touched);
    }

    if (state.contact.phone && !phoneRegex.test(state.contact.phone)) {
      this.addError(errors, 'contact.phone', 'pattern', includeAll, touched);
    }

    if (state.contact.ownerAddress.zip && !zipRegex.test(state.contact.ownerAddress.zip)) {
      this.addError(errors, 'contact.ownerAddress.zip', 'pattern', includeAll, touched);
    }

    const requiredEmergency: Array<[string, string]> = [
      ['emergencyContact.name', state.emergencyContact.name],
      ['emergencyContact.phoneOrNumber', state.emergencyContact.phoneOrNumber],
    ];

    requiredEmergency.forEach(([path, value]) => {
      if (!value.trim()) {
        this.addError(errors, path, 'required', includeAll, touched);
      }
    });

    if (state.emergencyContact.phoneOrNumber && !phoneRegex.test(state.emergencyContact.phoneOrNumber)) {
      this.addError(errors, 'emergencyContact.phoneOrNumber', 'pattern', includeAll, touched);
    }

    const feeValue = this.parseNumber(state.fees.totalEntryFees);
    if (!state.fees.totalEntryFees.trim()) {
      this.addError(errors, 'fees.totalEntryFees', 'required', includeAll, touched);
    } else if (feeValue !== null && feeValue <= 0) {
      this.addError(errors, 'fees.totalEntryFees', 'min', includeAll, touched);
    }

    if (options.includeSelections || includeAll) {
      if (state.selections.length === 0) {
        this.addError(errors, 'selections', 'minSelections', includeAll, touched);
      }
    }

    if (options.includeTerms || includeAll) {
      if (!state.terms.accepted) {
        this.addError(errors, 'terms.accepted', 'terms', includeAll, touched);
      }
    }

    formActions.setValidationErrors(errors);
    return errors;
  }

  private buildEntryUpdateRequest(state = formState.get()): EntryUpdateRequestDto {
    const feesValue = this.parseNumber(state.fees.totalEntryFees);

    return {
      dog: {
        ascaRegistrationNumber: state.dog.ascaRegistrationNumber || null,
        breed: state.dog.breed || null,
        registeredName: state.dog.registeredName || null,
        dob: state.dog.dob || null,
        color: state.dog.color || null,
        callName: state.dog.callName || null,
        sex: state.dog.sex || null,
        sire: state.dog.sire || null,
        dam: state.dog.dam || null,
        breeders: state.dog.breeders || null,
      },
      contact: {
        owners: state.contact.owners || null,
        ownerAddress: {
          street: state.contact.ownerAddress.street || null,
          city: state.contact.ownerAddress.city || null,
          state: state.contact.ownerAddress.state || null,
          zip: state.contact.ownerAddress.zip || null,
        },
        phone: state.contact.phone || null,
        handler: state.contact.handler || null,
        membershipNumber: state.contact.membershipNumber || null,
        junior: {
          dob: state.contact.junior.dob || null,
          memberId: state.contact.junior.memberId || null,
        },
      },
      emergencyContact: {
        name: state.emergencyContact.name || null,
        phoneOrNumber: state.emergencyContact.phoneOrNumber || null,
      },
      fees: {
        totalEntryFees: feesValue,
        currency: state.fees.currency || 'USD',
      },
    };
  }

  private applyServerErrors(errors?: Record<string, string[]>): void {
    if (!errors) return;

    const serverErrors: ValidationError[] = [];
    Object.entries(errors).forEach(([path, messages]) => {
      const message = messages.join(' ');
      serverErrors.push({ field: path, code: 'server', message });
      registrationActions.markTouched(path);
    });

    const existing = formState.get().validationErrors.filter((error) => error.code !== 'server');
    const existingMap = new Map(existing.map((error) => [error.field, error]));
    serverErrors.forEach((error) => {
      existingMap.set(error.field, error);
    });

    formActions.setValidationErrors(Array.from(existingMap.values()));
  }

  private handleSectionChange = (event: Event): void => {
    const detail = (event as CustomEvent<{ section: string; field: string; path: string; value: string }>).detail;
    if (!detail) return;

    if (detail.section === 'dog') {
      formActions.updateDog({ [detail.field]: detail.value } as Partial<DogFields>);
    }

    if (detail.section === 'contact') {
      if (detail.field.startsWith('ownerAddress.')) {
        const field = detail.field.replace('ownerAddress.', '') as keyof ContactFields['ownerAddress'];
        const addressUpdate: Partial<ContactFields['ownerAddress']> = {};
        addressUpdate[field] = detail.value;
        formActions.updateContact({ ownerAddress: addressUpdate });
      } else if (detail.field.startsWith('junior.')) {
        const field = detail.field.replace('junior.', '') as keyof ContactFields['junior'];
        const juniorUpdate: Partial<ContactFields['junior']> = {};
        juniorUpdate[field] = detail.value;
        formActions.updateContact({ junior: juniorUpdate });
      } else {
        formActions.updateContact({ [detail.field]: detail.value } as ContactUpdate);
      }
    }

    if (detail.section === 'emergencyContact') {
      formActions.updateEmergencyContact({ [detail.field]: detail.value } as Partial<EmergencyContactFields>);
    }

    if (detail.section === 'fees') {
      formActions.updateFees({ [detail.field]: detail.value } as Partial<FeesFields>);
    }
  };

  private handleSectionBlur = (event: Event): void => {
    const detail = (event as CustomEvent<{ path: string }>).detail;
    if (!detail?.path) return;
    registrationActions.markTouched(detail.path);
    this.validateForm();
    this.scheduleAutoSave();
  };

  private handleSelectionsChange = (event: Event): void => {
    const detail = (event as CustomEvent<{ selections: GridSelectionItem[] }>).detail;
    if (!detail) return;
    formActions.updateSelections(detail.selections);
    registrationActions.markTouched('selections');
    this.recalculateFees();
    this.validateForm({ includeSelections: true });
    this.scheduleSelectionSave(detail.selections);
  };

  private handleTermsChange = (event: Event): void => {
    const detail = (event as CustomEvent<{ accepted: boolean }>).detail;
    if (!detail) return;
    formActions.updateTerms({ accepted: detail.accepted });
    registrationActions.markTouched('terms.accepted');
    this.validateForm({ includeTerms: true });
  };

  private handlePremiumChange = (event: Event): void => {
    const detail = (event as CustomEvent<{ field: string; value: string | boolean }>).detail;
    if (!detail) return;
    if (detail.field === 'acknowledged') {
      formActions.updatePremium({ acknowledged: detail.value as boolean });
    } else if (detail.field === 'signature') {
      formActions.updatePremium({ signature: detail.value as string });
    }
  };

  private handleErrorSelected = (event: Event): void => {
    const detail = (event as CustomEvent<string>).detail;
    if (!detail) return;
    this.scrollToControl(detail);
  };

  private handleReviewErrors = (): void => {
    registrationActions.setValidationSummaryVisible(true);
    const errors = this.validateForm({ includeAll: true, includeSelections: true, includeTerms: true });
    if (errors[0]) {
      this.scrollToControl(errors[0].field);
    }
  };

  private scheduleAutoSave(): void {
    if (this.autoSaveTimer) {
      window.clearTimeout(this.autoSaveTimer);
    }
    this.autoSaveTimer = window.setTimeout(() => {
      void this.saveDraft();
    }, 500);
  }

  private scheduleSelectionSave(selections: GridSelectionItem[]): void {
    if (this.selectionSaveTimer) {
      window.clearTimeout(this.selectionSaveTimer);
    }
    this.selectionSaveTimer = window.setTimeout(() => {
      void this.saveSelections(selections);
    }, 500);
  }

  private async saveDraft(): Promise<void> {
    const state = formState.get();
    if (!state.entryId) {
      return;
    }

    formActions.setSaving(true);
    const response = await entriesApi.update(this.trialEventId, state.entryId, this.buildEntryUpdateRequest(state));
    formActions.setSaving(false);

    if (response.error) {
      this.applyServerErrors(response.error.errors);
      return;
    }

    toastActions.success('Draft saved');
    formActions.markClean();

    // Run validation after save
    this.validateForm();
    formActions.markValidationFresh();
  }

  private async saveSelections(selections: GridSelectionItem[]): Promise<void> {
    const state = formState.get();
    if (!state.entryId) return;

    formActions.setSaving(true);
    const response = await entriesApi.updateSelections(this.trialEventId, state.entryId, { items: selections });
    formActions.setSaving(false);

    if (response.error) {
      this.applyServerErrors(response.error.errors);
      return;
    }
  }

  private async submitEntry(): Promise<void> {
    const state = formState.get();
    if (!state.entryId || formState.get().isSubmitting) {
      return;
    }

    registrationActions.clearSubmitError();

    const errors = this.validateForm({ includeAll: true, includeSelections: true, includeTerms: true });
    if (errors.length > 0) {
      registrationActions.setValidationSummaryVisible(true);
      this.scrollToControl(errors[0].field);
      return;
    }

    formActions.setSubmitting(true);
    const response = await entriesApi.submit(this.trialEventId, state.entryId, {
      acceptTerms: true,
      termsVersion: state.terms.version || registrationState.terms.get()?.version || '',
    });
    formActions.setSubmitting(false);

    if (response.error) {
      this.applyServerErrors(response.error.errors);
      const supportId = response.supportId || response.error.traceId || '';
      registrationActions.setSubmitError(
        response.error.title || 'Unable to submit entry. Please try again.',
        supportId
      );
      return;
    }

    const entryId = response.data?.entryId || state.entryId;
    navigate(`/trial-events/${this.trialEventId}/confirmation/${entryId}`);
  }

  private scrollToControl(path: string): void {
    const element = this.$(`[data-control-path="${path}"]`) as HTMLElement | null;
    if (!element) return;
    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (typeof element.focus === 'function') {
      element.focus({ preventScroll: true });
    }
  }

  private renderLoading(): string {
    return `
      <section class="flex min-h-[400px] flex-col items-center justify-center gap-4" data-testid="loading-state">
        <dt-spinner size="large"></dt-spinner>
        <p class="text-sm font-medium text-neutral-600">Loading registration form...</p>
      </section>
    `;
  }

  private renderError(message: string): string {
    return `
      <section class="flex min-h-[400px] flex-col items-center justify-center" data-testid="error-state">
        <div class="max-w-md rounded-2xl border border-error-200 bg-error-50 p-6 text-center">
          <h2 class="text-lg font-semibold text-error-700">Unable to load registration</h2>
          <p class="mt-2 text-sm text-error-600">${message}</p>
          <dt-button variant="outline" class="mt-4" data-testid="retry-btn">Retry</dt-button>
        </div>
      </section>
    `;
  }

  private renderContent(trial: TrialEventSummaryDto): string {
    const state = formState.get();
    const errors = state.validationErrors;
    const selectionError = errors.find((error) => error.field === 'selections')?.message ?? '';
    const submitError = registrationState.submitErrorMessage.get();
    const submitSupportId = registrationState.submitSupportId.get();
    const validationStale = state.isValidationStale;

    const headerLabel = `${trial.formTemplate.organizationCode} ${trial.formTemplate.sportCode} ${trial.formTemplate.formCode}`;
    const location = trial.addressCity && trial.addressState
      ? `${trial.addressCity}, ${trial.addressState}`
      : trial.addressCity || trial.addressState || '';

    return `
      <section class="px-4 py-6" data-testid="registration-loaded">
        <div class="mx-auto flex w-full max-w-5xl flex-col gap-6">
          <dt-card variant="outlined">
            <div slot="header">
              <div class="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div class="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-500">${trial.hostClubName}</div>
                  <div class="mt-1 text-xs uppercase tracking-[0.3em] text-neutral-400">${headerLabel}</div>
                  <h1 class="mt-2 text-2xl font-bold text-neutral-900">${trial.name}</h1>
                  <div class="mt-2 grid gap-1 text-sm text-neutral-600">
                    <div><span class="font-semibold">Trial dates:</span> ${this.formatDateRange(trial)}</div>
                    ${location ? `<div><span class="font-semibold">Location:</span> ${location}</div>` : ''}
                  </div>
                </div>
                <div class="rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-right">
                  <div class="text-xs uppercase tracking-widest text-neutral-500">Entry Number</div>
                  <div class="mt-1 text-sm font-semibold text-neutral-800">${trial.trackingSlug}</div>
                </div>
              </div>
            </div>
            <div class="flex flex-col gap-6">
              <dt-validation-summary
                data-testid="validation-summary"
              ></dt-validation-summary>

              <form class="flex flex-col gap-6" novalidate>
                <dt-dog-section data-control-path="dog"></dt-dog-section>
                <dt-contact-section data-control-path="contact"></dt-contact-section>

                <section class="flex flex-col gap-3" data-control-path="selections">
                  <dt-class-selections-grid></dt-class-selections-grid>
                  ${selectionError ? `<p class="text-xs text-error-600">${selectionError}</p>` : ''}
                </section>

                <div class="grid gap-6 lg:grid-cols-2">
                  <dt-emergency-section data-control-path="emergencyContact"></dt-emergency-section>
                  <dt-fees-section data-control-path="fees"></dt-fees-section>
                </div>

                <dt-premium-section data-control-path="premium"></dt-premium-section>

                <dt-terms-section data-control-path="terms.accepted"></dt-terms-section>

                ${validationStale ? `
                  <div class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800" data-testid="validation-stale-message">
                    Re-validate required — save your draft to re-validate.
                  </div>
                ` : ''}

                <div class="flex flex-wrap items-center justify-end gap-3">
                  <dt-button variant="ghost" data-testid="review-errors">Review for errors</dt-button>
                  <dt-button variant="outline" data-testid="save-draft">Save Draft</dt-button>
                  <dt-button
                    variant="primary"
                    data-testid="submit-entry"
                  >Submit Entry</dt-button>
                </div>

                ${submitError ? `
                  <div class="rounded-xl border border-error-200 bg-error-50 p-4 text-sm text-error-700">
                    <div class="font-semibold">Submission failed</div>
                    <div class="mt-1">${submitError}</div>
                    ${submitSupportId ? `
                      <div class="mt-2 flex flex-wrap items-center gap-2 text-xs text-error-600">
                        <span>Support ID:</span>
                        <span class="font-mono font-semibold" data-testid="submit-support-id">${submitSupportId}</span>
                      </div>
                    ` : ''}
                  </div>
                ` : ''}
              </form>
            </div>
          </dt-card>
        </div>
      </section>
    `;
  }

  private updateRender(): void {
    const pageState = registrationComputed.pageState.get();
    let content = '';

    if (pageState === 'loading') {
      content = this.renderLoading();
    } else if (pageState === 'error') {
      content = this.renderError(registrationState.error.get() ?? 'Unable to load.');
    } else {
      const trial = registrationState.trial.get();
      if (trial) {
        content = this.renderContent(trial);
      }
    }

    this.render(content);
    this.attachEventListeners();
    this.assignSectionData();
  }

  private attachEventListeners(): void {
    const retryBtn = this.$('[data-testid="retry-btn"]');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => void this.loadRegistration());
    }

    const reviewBtn = this.$('[data-testid="review-errors"]');
    if (reviewBtn) {
      reviewBtn.addEventListener('click', () => this.handleReviewErrors());
    }

    const saveDraftBtn = this.$('[data-testid="save-draft"]');
    if (saveDraftBtn) {
      saveDraftBtn.addEventListener('click', () => void this.saveDraft());
    }

    const submitBtn = this.$('[data-testid="submit-entry"]');
    if (submitBtn) {
      submitBtn.addEventListener('click', () => void this.submitEntry());
    }

    const summary = this.$('dt-validation-summary');
    if (summary) {
      summary.addEventListener('error-selected', this.handleErrorSelected);
    }

    const dogSection = this.$('dt-dog-section');
    if (dogSection) {
      dogSection.addEventListener('section-change', this.handleSectionChange);
      dogSection.addEventListener('section-blur', this.handleSectionBlur);
    }

    const contactSection = this.$('dt-contact-section');
    if (contactSection) {
      contactSection.addEventListener('section-change', this.handleSectionChange);
      contactSection.addEventListener('section-blur', this.handleSectionBlur);
    }

    const emergencySection = this.$('dt-emergency-section');
    if (emergencySection) {
      emergencySection.addEventListener('section-change', this.handleSectionChange);
      emergencySection.addEventListener('section-blur', this.handleSectionBlur);
    }

    const feesSection = this.$('dt-fees-section');
    if (feesSection) {
      feesSection.addEventListener('section-change', this.handleSectionChange);
      feesSection.addEventListener('section-blur', this.handleSectionBlur);
    }

    const premiumSection = this.$('dt-premium-section');
    if (premiumSection) {
      premiumSection.addEventListener('section-change', this.handlePremiumChange);
    }

    const termsSection = this.$('dt-terms-section');
    if (termsSection) {
      termsSection.addEventListener('terms-change', this.handleTermsChange);
    }

    const grid = this.$('dt-class-selections-grid');
    if (grid) {
      grid.addEventListener('selections-change', this.handleSelectionsChange);
    }
  }

  private assignSectionData(): void {
    const state = formState.get();
    const errors = state.validationErrors;

    const summary = this.$('dt-validation-summary') as any;
    if (summary) {
      summary.errors = errors;
      summary.visible = registrationState.validationSummaryVisible.get();
    }

    const dogSection = this.$('dt-dog-section') as any;
    if (dogSection) {
      dogSection.dog = state.dog;
      dogSection.entryNumber = registrationState.trial.get()?.trackingSlug ?? null;
      dogSection.errors = errors;
    }

    const contactSection = this.$('dt-contact-section') as any;
    if (contactSection) {
      contactSection.contact = state.contact;
      contactSection.errors = errors;
    }

    const emergencySection = this.$('dt-emergency-section') as any;
    if (emergencySection) {
      emergencySection.emergency = state.emergencyContact;
      emergencySection.errors = errors;
    }

    const feesSection = this.$('dt-fees-section') as any;
    if (feesSection) {
      feesSection.fees = state.fees;
      feesSection.errors = errors;
      feesSection.feeBreakdown = this.currentFeeBreakdown;
    }

    const premiumSection = this.$('dt-premium-section') as any;
    if (premiumSection) {
      const trial = registrationState.trial.get();
      premiumSection.premiumPdfUrl = trial?.premiumPdfUrl ?? null;
      premiumSection.acknowledged = state.premium.acknowledged;
      premiumSection.signature = state.premium.signature;
    }

    const termsSection = this.$('dt-terms-section') as any;
    if (termsSection) {
      termsSection.terms = registrationState.terms.get();
      termsSection.accepted = state.terms.accepted;
      termsSection.error = errors.find((error) => error.field === 'terms.accepted')?.message ?? '';
    }

    const grid = this.$('dt-class-selections-grid') as any;
    if (grid) {
      grid.trial = registrationState.trial.get();
      grid.gridMetadata = registrationState.formMetadata.get()?.grid ?? null;
      grid.selections = state.selections;
    }

    const submitBtn = this.$('[data-testid="submit-entry"]') as any;
    if (submitBtn) {
      const hasErrors = errors.length > 0;
      const isStale = state.isValidationStale;
      const isSubmitting = state.isSubmitting;
      const isSaving = state.isSaving;
      submitBtn.disabled = !registrationComputed.termsAccepted.get() || isSubmitting || isSaving || isStale || hasErrors;
      submitBtn.loading = isSubmitting;
    }

    const saveDraftBtn = this.$('[data-testid="save-draft"]') as any;
    if (saveDraftBtn) {
      saveDraftBtn.loading = formState.get().isSaving;
    }
  }
}

customElements.define('dt-registration-page', RegistrationPage);
